import { apiClient } from '@/lib/api/client'
import type { ReportFilters } from '@/lib/services/helpdeskReportService'

export type TaskReportFilters = Omit<ReportFilters, 'group_by'> & {
  group_by?: 'module' | 'assignee' | 'team' | 'parent_ticket' | 'period' | 'all' | 'day' | 'week'
  scope?: 'mine' | 'team' | 'all'
}

export type TaskReportGroupRow = {
  dimension?: string
  dimension_key: string
  label: string
  count: number
  days_overdue_bucket?: string
}

export type TaskReportByUserRow = {
  user_id: number
  name: string
  open_count: number
  completed_count: number
  overdue_count: number
  avg_completion_hours: number | null
}

export type TaskReportByModuleRow = {
  action_type: string
  total_count: number
  completed_count: number
  completion_rate_percent: number
}

export type TaskReportSlaRow = {
  action_type?: string
  team_id?: number | null
  period?: string | null
  total_with_due: number
  on_time_count: number
  late_count: number
  open_overdue_count: number
  compliance_percent: number
}

export type TaskReportCompletionRow = {
  dimension: string
  dimension_key: string
  label: string
  completed_count: number
  avg_created_to_completed_hours: number
  median_created_to_completed_hours: number | null
  avg_accepted_to_completed_hours: number | null
  median_accepted_to_completed_hours: number | null
}

export type TaskReportProductivityRow = {
  user_id: number
  name: string
  period: string
  completed_count: number
  override_complete_count: number
  override_complete_rate_percent: number
  reopen_count: number
  reopen_rate_percent: number
}

export type TaskDashboardSummary = {
  scope: 'mine' | 'team' | 'all'
  total: number
  open_count: number
  completed_count: number
  overdue_count: number
  due_today_count: number
  due_soon_count: number
  by_status: Record<string, number>
  filters: Record<string, string | number>
}

function toQuery(filters: TaskReportFilters): Record<string, string> {
  const q: Record<string, string> = {}
  if (filters.client_id) q.client_id = String(filters.client_id)
  if (filters.from) q.from = filters.from
  if (filters.to) q.to = filters.to
  if (filters.team_id) q.team_id = String(filters.team_id)
  if (filters.group_by) q.group_by = filters.group_by
  if (filters.scope) q.scope = filters.scope
  if (filters.staging) q.staging = '1'
  return q
}

function unwrapData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export const taskReportService = {
  async getOpen(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/open', { params: toQuery(filters) })
    return res.data as { summary: { total_open: number }; groups: TaskReportGroupRow[]; filters: TaskReportFilters }
  },

  async getOverdue(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/overdue', { params: toQuery(filters) })
    return res.data as { summary: { total_overdue: number }; groups: TaskReportGroupRow[]; filters: TaskReportFilters }
  },

  async getByUser(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/by-user', { params: toQuery(filters) })
    return res.data as {
      summary: Record<string, number>
      data: TaskReportByUserRow[]
      filters: TaskReportFilters
    }
  },

  async getByModule(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/by-module', { params: toQuery(filters) })
    return res.data as {
      summary: Record<string, number>
      data: TaskReportByModuleRow[]
      filters: TaskReportFilters
    }
  },

  async getSlaCompliance(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/sla-compliance', { params: toQuery(filters) })
    return res.data as {
      summary: Record<string, number>
      data: TaskReportSlaRow[]
      filters: TaskReportFilters
    }
  },

  async getCompletionTime(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/completion-time', { params: toQuery(filters) })
    return res.data as {
      summary: Record<string, number>
      data: TaskReportCompletionRow[]
      filters: TaskReportFilters
    }
  },

  async getProductivity(filters: TaskReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks/productivity', { params: toQuery(filters) })
    return res.data as {
      summary: Record<string, number | Record<string, string>>
      data: TaskReportProductivityRow[]
      filters: TaskReportFilters
    }
  },

  async getSummary(scope: 'mine' | 'team' | 'all' = 'mine'): Promise<TaskDashboardSummary> {
    const res = await apiClient.get('/tasks/summary', { params: { scope } })
    return unwrapData<TaskDashboardSummary>(res.data)
  },
}
